import dayjs from '~/timeInit';

import type { ChainLpcLppSupplierDailyTrendPoint } from '~/types/services/priceParrot';

import { computeRatio } from './ratio';

export type TrendGranularity = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface TrendBucketPoint {
  bucket: string;
  ratio: number | null;
  pctLpc: number | null;
  pctLpp: number | null;
}

const ISO_DATE = 'YYYY-MM-DD';

function bucketStart(saleDate: string, granularity: TrendGranularity): string {
  const d = dayjs(saleDate, ISO_DATE);
  if (granularity === 'daily') {
    return saleDate;
  }
  if (granularity === 'weekly') {
    return d.startOf('week').format(ISO_DATE);
  }
  if (granularity === 'monthly') {
    return d.startOf('month').format(ISO_DATE);
  }
  if (granularity === 'yearly') {
    return d.startOf('year').format(ISO_DATE);
  }
  const quarterStartMonth = Math.floor(d.month() / 3) * 3;
  return d.month(quarterStartMonth).startOf('month').format(ISO_DATE);
}

// Sums bookings within each bucket before dividing, so the ratio reflects a
// meaningful sample size instead of single-digit daily booking noise.
export function bucketDailyTrend(
  daily: ReadonlyArray<ChainLpcLppSupplierDailyTrendPoint>,
  granularity: TrendGranularity,
): Array<TrendBucketPoint> {
  const buckets = new Map<string, { lpc: number; lpp: number; lpcViews: number | null; lppViews: number | null }>();
  for (const point of daily) {
    const key = bucketStart(point.sale_date, granularity);
    const existing = buckets.get(key) ?? { lpc: 0, lpp: 0, lpcViews: null, lppViews: null };
    existing.lpc += point.lpc_bookings;
    existing.lpp += point.lpp_bookings;
    if (point.lpc_views != null) {
      existing.lpcViews = (existing.lpcViews ?? 0) + point.lpc_views;
    }
    if (point.lpp_views != null) {
      existing.lppViews = (existing.lppViews ?? 0) + point.lpp_views;
    }
    buckets.set(key, existing);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucket, { lpc, lpp, lpcViews, lppViews }]) => ({
      bucket,
      ratio: computeRatio(lpc, lpp),
      pctLpc: computeRatio(lpcViews ?? 0, (lpcViews ?? 0) + (lppViews ?? 0)),
      pctLpp: computeRatio(lppViews ?? 0, (lpcViews ?? 0) + (lppViews ?? 0)),
    }));
}
